// auth.guard.ts

import { inject, Injectable } from "@angular/core";

import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from "@angular/router";

import {
  Observable,
  of,
} from "rxjs";

import {
  map,
  catchError,
} from "rxjs/operators";

import {
  UserPermissionService,
} from "../Common/UserPermission/userPermission.service";

import {
  ToastrService,
} from "ngx-toastr";

import {
  UserLoginService,
} from "../login/userlogin.service";

import {
  UserPermissionReadByUserIdRequestDTO,
} from "../Common/UserPermission/userpermission.interface";


@Injectable({
  providedIn: "root",
})
export class AuthGuard
  implements CanActivate {


  constructor(
    private router: Router,
  ) {}


  userPermissionService =
    inject(
      UserPermissionService,
    );


  toaster =
    inject(
      ToastrService,
    );


  userLoginService =
    inject(
      UserLoginService,
    );


  canActivate(

    route:
      ActivatedRouteSnapshot,

    state:
      RouterStateSnapshot,

  ): Observable<boolean> {


    // ============================================================
    // 1. TOKEN CHECK
    // EXISTING FLOW
    // ============================================================

    const token =
      localStorage.getItem(
        "access_token",
      );


    if (
      !token
    ) {

      this.forceLogout();

      return of(
        false,
      );
    }


    // ============================================================
    // 2. TOKEN EXPIRY CHECK
    // EXISTING FLOW
    // ============================================================

    if (
      this.isTokenExpired(
        token,
      )
    ) {

      this.toaster.warning(
        "Session expired. Please login again.",
      );


      this.forceLogout();


      return of(
        false,
      );
    }


    // ============================================================
    // 3. GET CURRENT USER
    // EXISTING FLOW
    // ============================================================

    const User =
      this.userLoginService
        .User();


    // ============================================================
    // 4. MENU LIST
    // EXISTING FLOW + SAFE NORMALIZATION
    // ============================================================

    const menulist =

      localStorage
        .getItem(
          "menuList",
        )
        ?.split(",")
        .map(
          (item) =>
            String(
              item || "",
            )
              .trim()
              .toLowerCase(),
        )
        .filter(
          Boolean,
        )

      ??

      [];


    // ============================================================
    // 5. CURRENT URL
    // ============================================================

    const cleanUrl =
      state.url
        .split("?")[0]
        .split("#")[0];


    const pathSegments =
      cleanUrl
        .split("/")
        .filter(
          Boolean,
        )
        .map(
          (item) =>
            decodeURIComponent(
              item,
            )
              .trim()
              .toLowerCase(),
        );


    const moduleCode =
      pathSegments[0];


    const pageCode =
      pathSegments[1];

      // Fee Management is a root-level protected route.
// Validate its actual menu permission, not an unrelated parent route.
if (moduleCode === "fee" && !pageCode) {
  const feeMenuAllowed =
    menulist.includes("fee") ||
    menulist.includes("fees");

  if (feeMenuAllowed) {
    return of(true);
  }
}


    // ============================================================
    // DEBUG
    // Can keep this.
    // Helps verify refresh permission flow.
    // ============================================================

    console.log(
      "==========================================",
    );

    console.log(
      "AuthGuard URL:",
      state.url,
    );

    console.log(
      "AuthGuard moduleCode:",
      moduleCode,
    );

    console.log(
      "AuthGuard pageCode:",
      pageCode,
    );

    console.log(
      "AuthGuard menuList:",
      menulist,
    );

    console.log(
      "==========================================",
    );


    // ============================================================
    // 6. EXISTING BASE / EMPTY ROUTE FLOW
    // ============================================================

    if (
      !moduleCode
      ||
      route.routeConfig?.path ===
        "base"
    ) {

      return of(
        true,
      );
    }


    // ============================================================
    // 7. BUILD POSSIBLE MENU CODES
    //
    // IMPORTANT REFRESH FIX
    //
    // URL:
    // /module-config/roles
    //
    // DB MenuCode:
    // module-config-roles
    //
    // Both formats are accepted.
    // ============================================================

    const pageMenuCandidates =
      this.getPageMenuCandidates(
        moduleCode,
        pageCode,
      );


    console.log(
      "AuthGuard page menu candidates:",
      pageMenuCandidates,
    );


    // ============================================================
    // 8. CHECK PARENT MODULE
    // ============================================================

    const moduleAllowed =
      menulist.includes(
        moduleCode,
      );


    // ============================================================
    // 9. PAGE CHECK
    //
    // Example:
    //
    // pageCode:
    // roles
    //
    // candidates:
    // roles
    // module-config-roles
    //
    // If either exists in menuList,
    // access is valid.
    // ============================================================

    const matchedPageMenuCode =
      pageMenuCandidates.find(
        (candidate) =>
          menulist.includes(
            candidate,
          ),
      );


    const pageAllowed =

      !pageCode

      ||

      !!matchedPageMenuCode;


    // ============================================================
    // 10. LOCAL MENU ACCESS SUCCESS
    // EXISTING LOGIC PRESERVED
    // ============================================================

    if (
      moduleAllowed
      &&
      pageAllowed
    ) {

      console.log(
        "AuthGuard local menu access allowed:",
        {
          moduleCode,
          pageCode,
          matchedPageMenuCode,
        },
      );


      return of(
        true,
      );
    }


    // ============================================================
    // 11. PERMISSION API MENU CODE
    //
    // IMPORTANT:
    //
    // For normal modules:
    // /school-admin/spe-school-dashboard
    // => spe-school-dashboard
    //
    // For Module Config:
    // /module-config/roles
    // => module-config-roles
    // ============================================================

    const permissionMenuCode =
      this.getPermissionMenuCode(
        moduleCode,
        pageCode,
        matchedPageMenuCode,
      );


    console.log(
      "AuthGuard permissionMenuCode:",
      permissionMenuCode,
    );


    // ============================================================
    // 12. IF ONLY PARENT ROUTE
    // ============================================================

    if (
      !pageCode
    ) {

      if (
        moduleAllowed
      ) {

        return of(
          true,
        );
      }


      /*
       * Parent itself is not available in menuList.
       * Existing behavior ultimately denies access.
       */
    }


    // ============================================================
    // 13. USER PERMISSION REQUEST
    // EXISTING FLOW
    // ============================================================

    let userPermissionData:
      UserPermissionReadByUserIdRequestDTO = {

      userId:
        User.userId,

      menuCode:
        permissionMenuCode,

    };


    console.log(
      "AuthGuard UserPermission Request:",
      userPermissionData,
    );


    return this
      .userPermissionService
      .UserPermission_readByUserId(
        userPermissionData,
      )
      .pipe(

       map(
  (res) => {

    console.log(
      "AuthGuard UserPermission Response:",
      res,
    );

    const body: any = res as any;

    const permissionItems =
      Array.isArray(body?.items) ? body.items :
      Array.isArray(body?.data?.items) ? body.data.items :
      Array.isArray(body?.data) ? body.data :
      [];

    if (permissionItems.length > 0) {

      this
        .userPermissionService
        .UserPermissions
        .next(
          permissionItems,
        );

      return true;
    }


            // ====================================================
            // ACCESS DENIED
            // EXISTING FLOW
            // ====================================================

            this.router.navigate(
              [
                "",
              ],
            );


            return false;
          },
        ),


        catchError(
          (error) => {


            console.error(
              "AuthGuard UserPermission Error:",
              error,
            );


            /*
             * Existing flow kept.
             */
            this
              .userLoginService
              .SignOutUser();


            return of(
              false,
            );
          },
        ),

      );
  }


  // ============================================================
  // NEW HELPER
  //
  // URL:
  // /module-config/roles
  //
  // Returns:
  // [
  //   "roles",
  //   "module-config-roles"
  // ]
  //
  // Existing other module routes remain unaffected.
  // ============================================================

  private getPageMenuCandidates(

    moduleCode:
      string,

    pageCode:
      string | undefined,

  ): string[] {


    if (
      !pageCode
    ) {

      return [];
    }


    const candidates:
      string[] = [];


    // ==========================================================
    // ORIGINAL PAGE CODE
    // ==========================================================

    candidates.push(
      pageCode,
    );


    // ==========================================================
    // MODULE CONFIG DB MENU CODE
    //
    // roles
    // => module-config-roles
    //
    // menu-master
    // => module-config-menu-master
    //
    // sub-roles
    // => module-config-sub-roles
    // ==========================================================

    if (
      moduleCode ===
      "module-config"
    ) {


      const moduleConfigCode =
        `${moduleCode}-${pageCode}`;


      if (
        !candidates.includes(
          moduleConfigCode,
        )
      ) {

        candidates.push(
          moduleConfigCode,
        );
      }
    }


    return candidates;
  }


  // ============================================================
  // NEW HELPER
  // CHOOSE CORRECT MENU CODE FOR PERMISSION API
  // ============================================================

  private getPermissionMenuCode(

    moduleCode:
      string,

    pageCode:
      string | undefined,

    matchedPageMenuCode:
      string | undefined,

  ): string {


    // ==========================================================
    // ALREADY MATCHED SOMETHING IN LOCAL MENU LIST
    // ==========================================================

    if (
      matchedPageMenuCode
    ) {

      return matchedPageMenuCode;
    }


    // ==========================================================
    // MODULE CONFIG
    //
    // This is the important refresh fix.
    // ==========================================================

    if (
      moduleCode ===
        "module-config"
      &&
      pageCode
    ) {

      return `${moduleCode}-${pageCode}`;
    }


    // ==========================================================
    // EXISTING ROUTES
    // ==========================================================

    return (
      pageCode
      ||
      moduleCode
    );
  }


  // ============================================================
  // EXISTING COMMENTED FLOW
  // KEPT
  // ============================================================

  // const attemptedUrl = state.url; // <-- this is the full attempted route

  // const match = attemptedUrl.match(/^\/real-estate\/listing\/\d+$/);

  // if (match) {
  //   const newUrl = attemptedUrl.replace("/real-estate", "/home");
  //   this.router.navigateByUrl(newUrl);
  // } else {
  //   this.router.navigate(["/login"]);
  // }

  // return false;


  // ============================================================
  // EXISTING LOGIN CHECK
  // ============================================================

  isLoggedIn():
    boolean {


    const token =
      localStorage.getItem(
        "access_token",
      );


    return (
      !!token
      &&
      !this.isTokenExpired(
        token,
      )
    );
  }


  // ============================================================
  // EXISTING TOKEN EXPIRY
  // ============================================================

  private isTokenExpired(
    token:
      string,
  ): boolean {


    try {


      const payload =
        JSON.parse(
          atob(
            token.split(".")[1],
          ),
        );


      if (
        !payload.exp
      ) {

        return true;
      }


      const expiryTime =
        payload.exp *
        1000;


      const currentTime =
        Date.now();


      return (
        currentTime >=
        expiryTime
      );


    } catch {


      return true;
    }
  }


  // ============================================================
  // EXISTING LOGOUT
  // ============================================================

  private forceLogout():
    void {


    this
      .userLoginService
      .SignOutUser();


    this.router.navigate(
      [
        "/login",
      ],
    );
  }
}